import React, { useContext, useEffect, useMemo, useState } from "react";

import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LightModeIcon from "@mui/icons-material/LightMode";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { Link, useLocation } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Environment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import Avatar from "../../assets/avatar";
import { PeriscopeLogo } from "../../assets/periscope-logo";
import { PermissionsContext } from "../../context/permissions-context";
import { openDevToolsOverlay } from "../../helpers/dev-tools-overlay-bridge";
import { getEnvironment } from "../../helpers/environment";
import useNavRedesign, {
  setNavRedesign,
} from "../../helpers/hooks/use-nav-redesign";
import { ThemePreference } from "../../helpers/theme-preference";
import mainStyles from "../../styles";
import Logout from "../okta/logout";
import CreateOrgDialog from "../org-creation/create-org-dialog";
import CollapsibleNavItem from "./components/collapsible-nav-item";
import DisplayVersion from "./components/display-version";
import NavList from "./components/nav-list";
import Notifications from "./components/notifications";
import { TopBarIconButton } from "./components/top-bar-icon-button";
import TopBarSearch from "./components/top-bar-search";
import {
  collapsedDrawerWidthSm,
  collapsedDrawerWidthXs,
  drawerWidth,
  topBarHeight,
  useNavStyles,
} from "./styles";

interface NavigationProps {
  isDarkMode: boolean;
  handleThemeChange: (preference: ThemePreference) => void;
}

interface EnvBadge {
  label: string;
  sx: Record<string, unknown>;
}

// The permanently-dark sidenav rail colour. The top bar also uses it in dark
// mode so the two share a seamless dark surface (in light mode the top bar
// stays on the theme's paper colour).
const RAIL_COLOR = "#0a0b0d";

// The circular collapse/expand chevron that floats on the sidenav's right edge.
// It is offset left by half its width so it straddles the drawer border, and
// its top is tuned so its centre lines up with the first nav item's row
// (header height + the list's top padding + half a nav row).
const CHEVRON_SIZE = 26;
const CHEVRON_TOP = topBarHeight + 22;

// Merged pill, e.g. "DEV | v1.5.274". Falls back to plain version text with
// no chip when there's no env badge to merge it with (Environment.UNSPECIFIED).
const renderEnvAndVersionBadge = (
  envBadge: EnvBadge | null,
): React.ReactNode =>
  envBadge ? (
    <Box
      component="span"
      sx={{
        ...envBadge.sx,
        fontSize: 11,
        fontWeight: 600,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {envBadge.label}
      <Box component="span" sx={{ opacity: 0.7, fontWeight: 400 }}>
        |
      </Box>
      <DisplayVersion />
    </Box>
  ) : (
    <Box component="span" sx={{ color: "text.secondary" }}>
      <DisplayVersion />
    </Box>
  );

const Navigation = ({ isDarkMode, handleThemeChange }: NavigationProps) => {
  const navRedesign = useNavRedesign();
  const theme = useTheme();
  // The sidenav renders as a permanently dark rail in both light and dark app
  // themes (Apex-style); the top bar follows the app theme. A nested dark theme
  // lets every token-based child of the drawer (nav text, icons, dividers)
  // restyle for the dark surface automatically. Content dialogs declared inside
  // the drawer are restored to the app theme below so they don't inherit it.
  const frameTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark",
          primary: { main: "#8983ff" },
          background: { paper: RAIL_COLOR, default: RAIL_COLOR },
          divider: "rgba(255, 255, 255, 0.1)",
        },
      }),
    [],
  );
  const periscopeLogoSize = 28;
  const defaultOktaUser = useMemo(() => {
    return { email: "", name: "", given_name: "", sub: "" };
  }, []);
  const classes = useNavStyles();
  const mainClasses = mainStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const isDevEnv = getEnvironment() === Environment.DEVELOPMENT;
  const { hasPermissions } = useContext(PermissionsContext);
  const canCreateOrg =
    hasPermissions([ActionType.CREATE_ORGANISATION]) &&
    getEnvironment() !== Environment.PRODUCTION;
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState<UserClaims>(defaultOktaUser);

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      setUserInfo(defaultOktaUser);
    } else {
      oktaAuth.getUser().then((info: UserClaims) => {
        if (info && !userInfo.sub) {
          setUserInfo(info);
        }
      });
    }
  }, [authState, oktaAuth, defaultOktaUser, userInfo.sub]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getEnvBadge = () => {
    switch (getEnvironment()) {
      case Environment.DEVELOPMENT:
        return { label: "DEV", sx: mainClasses.purpleRain };
      case Environment.SANDPIT:
        return { label: "SANDPIT", sx: mainClasses.yellowSubmarine };
      case Environment.PRODUCTION:
        return { label: "PRODUCTION", sx: mainClasses.greenDay };
      default:
        return null;
    }
  };

  const envBadge = getEnvBadge();

  const [open, setOpen] = useState(true);

  useEffect(() => {
    const data = window.localStorage.getItem("SIDE_NAV_OPEN");
    if (data !== null) setOpen(JSON.parse(data));
  }, []);

  useEffect(() => {
    window.localStorage.setItem("SIDE_NAV_OPEN", JSON.stringify(open));
  }, [open]);

  const location = useLocation();
  const isOrgPage = /^\/organisation\/[^/]+\/[^/]+/.test(location.pathname);
  // In the redesign, the org's own grouped sub-nav is the primary navigation,
  // so collapse the global rail to icons while viewing an org. This is kept
  // separate from the persisted SIDE_NAV_OPEN preference used elsewhere.
  const collapseGlobalRail = navRedesign && isOrgPage;
  const [orgRailOpen, setOrgRailOpen] = useState(false);
  const drawerOpen = collapseGlobalRail ? orgRailOpen : open;

  useEffect(() => {
    if (collapseGlobalRail) {
      setOrgRailOpen(false);
    }
  }, [location.pathname, collapseGlobalRail]);

  const handleDrawerOpen = () => {
    if (collapseGlobalRail) {
      setOrgRailOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleDrawerClose = () => {
    if (collapseGlobalRail) {
      setOrgRailOpen(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Box sx={classes.root}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          height: topBarHeight,
          zIndex: theme.zIndex.drawer + 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: isDarkMode ? RAIL_COLOR : "background.paper",
          transition: theme.transitions.create(["left"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          left: drawerOpen ? drawerWidth : collapsedDrawerWidthXs,
          [theme.breakpoints.up("sm")]: {
            left: drawerOpen ? drawerWidth : collapsedDrawerWidthSm,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <TopBarSearch />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {renderEnvAndVersionBadge(envBadge)}
          <Notifications />
          <Tooltip title="Try the new navigation layout (beta)">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ViewSidebarIcon fontSize="small" />
              <Switch
                checked={navRedesign}
                onChange={(event) => setNavRedesign(event.target.checked)}
                inputProps={{ "aria-label": "Toggle new navigation layout" }}
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <TopBarIconButton
              aria-label="Toggle theme"
              onClick={() => handleThemeChange(isDarkMode ? "light" : "dark")}
            >
              {isDarkMode ? (
                <LightModeIcon fontSize="small" />
              ) : (
                <DarkModeIcon fontSize="small" />
              )}
            </TopBarIconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Collapse/expand control: a circular chevron floating on the sidenav's
          right edge, just below the header (Apex style). Rendered outside the
          dark frame so it reads as a light control straddling the border. */}
      <Tooltip title={drawerOpen ? "Collapse sidebar" : "Expand sidebar"}>
        <IconButton
          aria-label={drawerOpen ? "Collapse sidebar" : "Expand sidebar"}
          onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}
          size="small"
          sx={{
            position: "fixed",
            top: CHEVRON_TOP,
            left:
              (drawerOpen ? drawerWidth : collapsedDrawerWidthXs) -
              CHEVRON_SIZE / 2,
            [theme.breakpoints.up("sm")]: {
              left:
                (drawerOpen ? drawerWidth : collapsedDrawerWidthSm) -
                CHEVRON_SIZE / 2,
            },
            zIndex: theme.zIndex.drawer + 2,
            width: CHEVRON_SIZE,
            height: CHEVRON_SIZE,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            boxShadow: 1,
            // Match the drawer's own width animation so the chevron stays glued
            // to the edge: the drawer expands with enteringScreen and collapses
            // with leavingScreen.
            transition: theme.transitions.create(["left"], {
              easing: theme.transitions.easing.sharp,
              duration: drawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
            "&:hover": { bgcolor: "background.paper", boxShadow: 3 },
          }}
        >
          {drawerOpen ? (
            <ChevronLeftIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <ThemeProvider theme={frameTheme}>
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: {
              ...classes.drawerPaper,
              ...(!drawerOpen && classes.drawerPaperClose),
            },
          }}
          open={drawerOpen}
        >
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              disableTouchRipple
              sx={{
                ...mainClasses.removeVisitedLinkStyle,
                justifyContent: drawerOpen ? "initial" : "center",
                height: topBarHeight,
                overflow: "hidden",
                py: 0,
                borderBottom: 1,
                borderColor: "divider",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 2 : 0,
                  justifyContent: "center",
                }}
              >
                <PeriscopeLogo size={periscopeLogoSize} />
              </ListItemIcon>
              {drawerOpen && (
                <Typography
                  variant="h6"
                  sx={{
                    lineHeight: "normal",
                    fontWeight: 700,
                    fontSize: "1.15rem",
                  }}
                >
                  Periscope
                </Typography>
              )}
            </ListItemButton>
          </ListItem>

          {/* Main nav items — the only scrollable region of the drawer */}
          <List sx={{ ...classes.navScrollArea, pt: 1.5 }}>
            <NavList collapsed={!drawerOpen} />
          </List>

          {canCreateOrg && (
            <>
              {/* Separator so the pinned Create Org action reads as distinct
                  from the scrollable nav items above it. Bottom margin matches
                  the item's own bottom margin below, so the button gets even
                  spacing on both sides instead of hugging the divider. */}
              <Divider sx={{ mt: 1, mb: 0.5 }} />
              <CollapsibleNavItem
                icon={<AddBusinessIcon />}
                label="Create Org"
                onClick={() => setCreateOrgOpen(true)}
                collapsed={!drawerOpen}
              />
              <ThemeProvider theme={theme}>
                <CreateOrgDialog
                  open={createOrgOpen}
                  onClose={() => setCreateOrgOpen(false)}
                />
              </ThemeProvider>
            </>
          )}

          {/* Bottom user avatar and logout. Only bottom margin on this divider:
              the item above (Create Org, or the last nav item) already carries
              its own bottom margin, so adding top margin here would double up. */}
          <List disablePadding sx={{ pb: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleMenuClick}
                // Match the sizing of the nav items (see CollapsibleNavItem).
                sx={{
                  minHeight: drawerOpen ? 28 : 40,
                  justifyContent: drawerOpen ? "initial" : "center",
                  px: drawerOpen ? 2 : 0,
                  mx: drawerOpen ? 1 : "auto",
                  ...(!drawerOpen && { width: 40 }),
                  borderRadius: 2,
                  color: "text.secondary",
                  "& .MuiListItemIcon-root": { color: "text.secondary" },
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.06)" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 0,
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Avatar height={20} width={20} />
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText
                    primary={userInfo.given_name}
                    // This will break up long names onto new line
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                      sx: { whiteSpace: "normal", wordWrap: "break-word" },
                    }}
                  />
                )}
                {drawerOpen && <ExitToAppIcon fontSize="small" />}
              </ListItemButton>
            </ListItem>
          </List>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            marginThreshold={5}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Box sx={{ width: 230 }}>
              {isDevEnv && (
                <Tooltip title="Dev Tools (⌘⇧`)" placement="left">
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openDevToolsOverlay("settings");
                    }}
                  >
                    <ListItemIcon>
                      <DeveloperModeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dev Tools" />
                  </MenuItem>
                </Tooltip>
              )}
              <Logout />
            </Box>
          </Menu>
        </Drawer>
      </ThemeProvider>
    </Box>
  );
};

export default Navigation;
